import { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, CalendarDays } from "lucide-react";

const DAY_MS = 24 * 3600 * 1000;

const startOfWeek = (offset = 0) => {
  const now = new Date();
  const d = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const monday = new Date(d.getTime() - ((d.getDay() + 6) % 7) * DAY_MS + offset * 7 * DAY_MS);
  return monday;
};

export const WeekSchedule = ({ bookings }) => {
  const [offset, setOffset] = useState(0);
  const monday = startOfWeek(offset);
  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => new Date(monday.getTime() + i * DAY_MS)), [monday]);
  const end = new Date(monday.getTime() + 7 * DAY_MS);

  const weekBookings = (bookings || []).filter((b) => {
    if (!b.scheduled_at || b.status === "cancelled") return false;
    const t = new Date(b.scheduled_at);
    return t >= monday && t < end;
  });
  const byDay = (day) =>
    weekBookings
      .filter((b) => new Date(b.scheduled_at).toDateString() === day.toDateString())
      .sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at));
  const recurringCount = weekBookings.filter((b) => b.frequency).length;
  const todayStr = new Date().toDateString();
  const rangeLabel = `${monday.toLocaleDateString(undefined, { month: "short", day: "numeric" })} – ${new Date(end.getTime() - DAY_MS).toLocaleDateString(undefined, { month: "short", day: "numeric" })}`;

  return (
    <div className="card-metal p-6 mt-8" data-testid="week-schedule">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-heading font-black text-xl flex items-center gap-2">
          <CalendarDays className="w-5 h-5 text-[#9b111e]" /> My Week
        </h2>
        <div className="flex items-center gap-2">
          <button onClick={() => setOffset((o) => o - 1)} className="p-1.5 rounded-full hover:bg-slate-100 transition-colors" data-testid="week-prev" title="Previous week">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button onClick={() => setOffset(0)} className={`text-sm font-semibold px-2 ${offset === 0 ? "text-[#9b111e]" : "text-slate-500 hover:text-slate-700"}`} data-testid="week-range">
            {offset === 0 ? "This week" : rangeLabel}
          </button>
          <button onClick={() => setOffset((o) => o + 1)} className="p-1.5 rounded-full hover:bg-slate-100 transition-colors" data-testid="week-next" title="Next week">
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
      <p className="text-xs text-slate-500 mt-1" data-testid="week-summary">
        {rangeLabel} · {weekBookings.length} visit{weekBookings.length === 1 ? "" : "s"}
        {recurringCount > 0 && <> · 🔁 {recurringCount} recurring</>}
      </p>
      <div className="overflow-x-auto mt-3 -mx-1 px-1">
        <div className="grid grid-cols-7 gap-2 min-w-[840px]">
          {days.map((day) => {
            const items = byDay(day);
            const isToday = day.toDateString() === todayStr;
            return (
              <div key={day.toISOString()} className={`rounded-lg border p-2 min-h-[110px] ${isToday ? "border-[#9b111e] bg-red-50/50" : "border-slate-200 bg-white"}`} data-testid={`week-day-${day.getDay()}`}>
                <p className={`text-[11px] font-black uppercase ${isToday ? "text-[#9b111e]" : "text-slate-500"}`}>
                  {day.toLocaleDateString(undefined, { weekday: "short" })}{" "}
                  <span className="font-semibold">{day.getDate()}</span>
                  {isToday && <span className="ml-1">· today</span>}
                </p>
                <div className="space-y-1.5 mt-1.5">
                  {items.length === 0 && <p className="text-[11px] text-slate-300">—</p>}
                  {items.length >= 2 && (() => {
                    const cities = [...new Set(items.map((b) => b.client_area?.city).filter(Boolean))];
                    return cities.length === 1 ? (
                      <p className="text-[10px] font-bold text-green-700 bg-green-50 border border-green-200 rounded px-1.5 py-0.5" data-testid={`same-area-day-${day.getDay()}`}>
                        🚗 Same-area day · {cities[0]}
                      </p>
                    ) : null;
                  })()}
                  {items.map((b) => (
                    <div key={b.id} className={`rounded-md px-1.5 py-1 text-[11px] leading-tight border ${b.frequency ? "bg-blue-50 border-blue-200" : "bg-slate-50 border-slate-200"}`} data-testid={`week-visit-${b.id}`}>
                      <p className="font-bold text-slate-800">
                        {new Date(b.scheduled_at).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}
                        {b.client_subscriber && <span title="Subscribe & Save member"> 💎</span>}
                        {b.frequency && <span title={`Recurring · ${b.frequency}`}> 🔁</span>}
                        {b.client_spotless && <span title="Spotless Home client"> ✨</span>}
                      </p>
                      <p className="text-slate-600 truncate">{b.client_nickname || b.client_name}</p>
                      {(b.client_area?.city || b.client_area?.zip) && (
                        <p className="text-blue-600 truncate font-semibold" data-testid={`week-visit-area-${b.id}`}>
                          📍 {b.client_area.city || ""}{b.client_area.city && b.client_area.zip ? " " : ""}{b.client_area.zip || ""}
                        </p>
                      )}
                      <p className="text-slate-400 truncate">{b.service}</p>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <p className="text-[11px] text-slate-400 mt-2">💎 Subscribe & Save member · 🔁 recurring visit · ✨ Spotless Home client · blue cards repeat automatically</p>
    </div>
  );
};
